import { formatPhone } from '@zyno-io/vue-foundation';
import { compact } from 'lodash';
import { defineStore } from 'pinia';
import { computed, watch, type ComputedRef } from 'vue';

import type { ICurrentWorkstationExtensionResponse, IExtensionResponse, INumberResponse, ITalkPermissions } from '@/openapi-clients-generated/talk';

import { GlobalState } from '@/global';
import { DataCache } from '@/shared/data/cache';

export const useTalkStore = defineStore('talk', {
    state: () => ({
        selectedNumbers: null as (INumberResponse | IExtensionResponse)[] | null,
        callIdFilter: null as string | null
    }),

    getters: {
        hasSelectedNumbers(state) {
            return state.selectedNumbers !== null && state.selectedNumbers.length > 0;
        },

        hasSingleSelectedNumber(state) {
            return state.selectedNumbers !== null && state.selectedNumbers.length === 1;
        },

        selectedNumberIds(state) {
            return state.selectedNumbers?.map(n => n.id) ?? null;
        },

        selectedNumberIdsStr(state) {
            return state.selectedNumbers ? state.selectedNumbers.map(n => n.id).join(',') || '-' : undefined;
        },

        isTalkAdmin() {
            return GlobalState.tenant.value?.isAdmin;
        }
    }
});

export const useTalkHelpers = () => {
    const access = DataCache.getRef('talkAccess');
    const numbers = DataCache.getRef('talkNumbers');
    const extensions = DataCache.getRef('talkExtensions');

    const hasNumberPermission = (number: INumberResponse | IExtensionResponse, permissionKey: keyof ITalkPermissions) => {
        if (GlobalState.tenant.value?.isAdmin) return true;
        if (!access.value) return undefined;
        if ('extension' in number) {
            return (
                access.value?.find(a => a.objectType === 'extension' && a.objectId === null)?.permissions?.[permissionKey] ??
                access.value?.find(a => a.objectType === 'extension' && a.objectId === number.id)?.permissions?.[permissionKey] ??
                false
            );
        }
        if ('phoneNumber' in number) {
            return (
                access.value?.find(a => a.objectType === 'number' && a.objectId === null)?.permissions?.[permissionKey] ??
                access.value?.find(a => a.objectType === 'number' && a.objectId === number.id)?.permissions?.[permissionKey] ??
                false
            );
        }
        return false;
    };

    const hasOneOfNumberPermissions = (number: INumberResponse | IExtensionResponse, permissionKeys: (keyof ITalkPermissions)[]) => {
        return permissionKeys.some(pk => hasNumberPermission(number, pk));
    };

    const extensionsByNumberId = computed(() => {
        const map: Record<string, IExtensionResponse> = {};
        for (const ext of extensions.value ?? []) {
            if (ext.linkedNumberId) {
                map[ext.linkedNumberId] = ext;
            }
        }
        return map;
    });

    const formatNumber = (number: INumberResponse | IExtensionResponse | { $my: true; deletedAt: null }) => {
        const parts: (string | null)[] = [];
        if ('$my' in number) {
            parts.push('My Numbers');
        } else if ('extension' in number) {
            if (number.enableSip) {
                parts.push(`Ext ${number.extension}`);
            } else if (number.enableVoicemail) {
                parts.push(`VM ${number.extension}`);
            } else {
                parts.push(`X ${number.extension}`);
            }
            parts.push(number.displayName || number.name);
        } else if ('phoneNumber' in number) {
            parts.push(formatPhone(number.phoneNumber));
            const mappedExtension = extensionsByNumberId.value[number.id];
            if (mappedExtension) {
                parts.push(`Ext ${mappedExtension.extension}: ${mappedExtension.displayName || mappedExtension.name}`);
            } else {
                parts.push(number.description);
            }
        }
        const label = compact(parts).join(' • ');
        if (number.deletedAt) return `${label} (deleted)`;
        return label;
    };

    const incrementUnreadCount = (type: 'voicemail' | 'sms' | 'fax', numberOrExtId: string, increment: number) => {
        if (type === 'voicemail') {
            const target = extensions.value?.find(n => n.id === numberOrExtId);
            if (target) {
                target['voicemailUnreadCount'] += increment;
            }
        } else {
            const target = numbers.value?.find(n => n.id === numberOrExtId);
            if (target) {
                target[type === 'sms' ? 'smsUnreadCount' : 'faxUnreadCount'] += increment;
            }
        }
    };

    /** Admins see every lot; everyone else needs an explicit `parkingLot.view` grant (all-lots or a specific one). */
    const canViewParkingLots = computed<boolean | undefined>(() => {
        if (GlobalState.tenant.value?.isAdmin) return true;
        if (!access.value) return undefined;
        return access.value.some(a => a.objectType === 'parkingLot' && a.permissions['parkingLot.view']);
    });

    /**
     * Admins see every call; everyone else needs a `callHistory` grant on some number or extension
     * (all-numbers/all-extensions or a specific one). `talk.vue` / `talk-header.vue` compute an
     * equivalent from the loaded numbers/extensions lists for their nav-gating purposes; this
     * composite-access-based version is simpler for a screen (parking) that doesn't load those lists.
     */
    const hasCallHistoryAccess = computed<boolean | undefined>(() => {
        if (GlobalState.tenant.value?.isAdmin) return true;
        if (!access.value) return undefined;
        return access.value.some(a => (a.objectType === 'number' || a.objectType === 'extension') && a.permissions.callHistory);
    });

    return {
        hasNumberPermission,
        hasOneOfNumberPermissions,
        formatNumber,
        incrementUnreadCount,
        canViewParkingLots,
        hasCallHistoryAccess
    };
};

export const useTalkSoftphoneHelpers = () => {
    const access = DataCache.getRef('talkAccess');
    const extensions = DataCache.getRef('talkExtensions');

    const isSoftphoneAuthorized = computed(() => GlobalState.tenant.value?.isAdmin || access.value?.some(a => a.permissions.softphone ?? false));
    const hasSoftphoneNumbers = computed(() => extensions.value?.some(ext => ext.enableSoftphone) ?? false);
    const canUseSoftphone = computed(() => (access.value && extensions.value ? isSoftphoneAuthorized.value && hasSoftphoneNumbers.value : undefined));

    return { isSoftphoneAuthorized, hasSoftphoneNumbers, canUseSoftphone };
};

// Installed once, on the first construction of `useTalkWorkstationExtension()` — not per call-site —
// so the several call sites (extension-live-calls.manager.ts, softphone.manager.ts, the dock, the
// dialer, admin workstation screens) don't stack duplicate watchers/timers. Two independent
// self-healing behaviors for a workstation session:
//  - A failed `/current` load must not strand the session in the fail-closed "pending" state for
//    good: `talk.access` (app.auth.ts) retries it on the next SSE access event, but that may not
//    arrive at all — back it with a bounded exponential retry too.
//  - A linked extension id that isn't in the (possibly stale) `talkExtensions` cache yet — e.g. the
//    extension was created moments before it was linked — gets exactly one background reload of that
//    cache per id, never a retry loop.
let workstationExtensionSideEffectsInstalled = false;
let workstationExtensionRetryAttempt = 0;
let workstationExtensionRetryTimer: ReturnType<typeof setTimeout> | undefined;
let lastReloadedForMissingExtensionId: string | null = null;

function ensureWorkstationExtensionSideEffects(current: ComputedRef<ICurrentWorkstationExtensionResponse | undefined>) {
    if (workstationExtensionSideEffectsInstalled) return;
    workstationExtensionSideEffectsInstalled = true;

    watch(
        // Only touches the cache while on a workstation — same lazy-access invariant as `current` below.
        () => (GlobalState.isWorkstation ? DataCache.getRef('talkWorkstationExtension').loadError : undefined),
        loadError => {
            if (workstationExtensionRetryTimer !== undefined) {
                clearTimeout(workstationExtensionRetryTimer);
                workstationExtensionRetryTimer = undefined;
            }
            if (!loadError) {
                workstationExtensionRetryAttempt = 0;
                return;
            }
            workstationExtensionRetryAttempt += 1;
            const delayMs = Math.min(30_000, 1_000 * 2 ** (workstationExtensionRetryAttempt - 1));
            workstationExtensionRetryTimer = setTimeout(() => DataCache.reloadCache('talkWorkstationExtension', true), delayMs);
        }
    );

    watch(
        () => {
            const extensionId = current.value?.extensionId ?? null;
            if (!extensionId) return null;
            const extensions = DataCache.getRef('talkExtensions').value;
            if (!extensions) return null; // still loading — not (yet) "missing"
            return extensions.some(ext => ext.id === extensionId) ? null : extensionId;
        },
        missingExtensionId => {
            if (!missingExtensionId || lastReloadedForMissingExtensionId === missingExtensionId) return;
            lastReloadedForMissingExtensionId = missingExtensionId;
            DataCache.reloadCache('talkExtensions', true);
        }
    );
}

/**
 * The current session's workstation↔extension binding (see `docs/talk-workstation-extension-plan.md`
 * §0a). Only touches `talkWorkstationExtension` while `GlobalState.isWorkstation` — a browser session
 * never makes this request.
 *
 * `isSharedWorkstation` gates the exclusive "shared workstation" phone behavior: on a shared
 * workstation the phone surfaces use ONLY this binding, whether or not it's linked. On a dedicated
 * (account-bound) workstation the binding is ignored outright and the caller falls back to ordinary
 * personal (starred/primary) behavior — `workstationExtensionId` reads null there regardless of what
 * the server returns, so callers never need to check `isSharedWorkstation` twice.
 *
 * `blocksPersonalFallback` is the gate every phone surface should actually use instead of
 * `isSharedWorkstation` alone: it also covers `isWorkstationLinkPending`, so a workstation session
 * fails CLOSED (no personal starred rows, no personal incoming ring, no personal dialer extensions)
 * while `/current` hasn't resolved yet, rather than briefly behaving like a browser session.
 */
export const useTalkWorkstationExtension = () => {
    // Every DataCache access below is lazy (inside a computed), never at call time — this composable
    // is called from module-scope singleton managers (extension-live-calls.manager.ts,
    // softphone.manager.ts) as well as component setup, and merely constructing it must stay free of
    // side effects for a non-workstation session.
    const current = computed(() => {
        if (!GlobalState.isWorkstation) return undefined;
        return DataCache.getRef('talkWorkstationExtension').value;
    });

    const isSharedWorkstation = computed(() => current.value?.shared === true);

    // True only while a workstation session's `/current` lookup hasn't resolved (in flight, or failed
    // and awaiting the retry above). See docs/talk-workstation-extension-plan.md finding #3.
    const isWorkstationLinkPending = computed(() => GlobalState.isWorkstation && current.value === undefined);

    const blocksPersonalFallback = computed(() => isSharedWorkstation.value || isWorkstationLinkPending.value);

    // Cross-checked against the live extensions list rather than trusted verbatim: a stale link
    // (extension deleted, SIP disabled since) must not surface as a usable "Call From".
    const workstationExtension = computed(() => {
        if (!isSharedWorkstation.value) return undefined;
        const extensionId = current.value?.extensionId;
        if (!extensionId) return undefined;
        return DataCache.getRef('talkExtensions').value?.find(ext => ext.id === extensionId && !ext.deletedAt && ext.enableSip);
    });

    const workstationExtensionId = computed(() => workstationExtension.value?.id ?? null);

    ensureWorkstationExtensionSideEffects(current);

    return { current, isSharedWorkstation, isWorkstationLinkPending, blocksPersonalFallback, workstationExtensionId, workstationExtension };
};
